import { Box, CircularProgress, Typography } from "@mui/material";
import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import DeleteChatDialog from "Samarth Module/components/SamarthAi/DeleteChatDialog";
import RefreshIcon from "../../../assets/images/refresh.svg";
import ChatIcon from "../../../assets/images/chat.svg";
import ExportIcon from "../../../assets/images/export.svg";
import ExpandIcon from "../../../assets/images/expandmore.svg";
import DragDropIcon from "../../../assets/images/drag-drop.svg";
import DeleteIcon from "../../../assets/images/delete-blue.svg";
import TableIcon from "../../../assets/images/tableIcon.svg";
import GraphIcon from "../../../assets/images/graphIcon.svg";
import ExpandLessIcon from "../../../assets/images/expandless.svg";
import BackArrow from "../../../assets/images/blue-back-arrow.svg";
import EditIcon from "../../../assets/images/blue-edit.svg";

import styles from "./Dashboard.module.css";
import {
  dashboardOrderPlacementV2,
  dashboardRefresh,
  deleteDashboardListV2,
  getDashboardListById,
} from "Samarth Module/api/samarthAiAPI";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import OverallView from "Samarth Module/components/SamarthAi/Graphs/OverallView";
import ReusableSnackbar from "Samarth Module/components/SamarthAi/ReusableSnackbar";

// ── Static helpers (outside component) ──
const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const isToday = date.toDateString() === new Date().toDateString();
  return `Last Refreshed at ${
    isToday ? "Today" : date.toLocaleDateString("en-IN")
  }, ${date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const formatLabel = (text) =>
  text
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();

  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  const time = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;

  return date.toLocaleString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getColumnCount = (dashboardData) => {
  const statement =
    dashboardData?.query_result?.statement_response ||
    dashboardData?.statement_response ||
    {};
  const columns = statement?.manifest?.schema?.columns;
  if (Array.isArray(columns) && columns.length > 0) return columns.length;
  const dataArray =
    statement?.result?.data_array ||
    dashboardData?.query_result?.statement_response?.result?.data_array;
  if (Array.isArray(dataArray) && dataArray.length > 0) {
    return Array.isArray(dataArray[0]) ? dataArray[0].length : 0;
  }
  return 0;
};

const getAutoMd = (item, view) => {
  if (item.view_type === "table") {
    const colCount = getColumnCount(item.dashboard_data);

    if (item.view_size) return item.view_size;

    if (colCount > 0) {
      if (colCount <= 3) return 3;
      if (colCount > 4) return 12;
      return 6;
    }

    return 6;
  }

  return item.view_size || 6;
};

const formatWidgets = (data, view) => {
  if (!data?.length) return [];
  const uniqueMap = new Map();
  data.forEach((item) => {
    if (!uniqueMap.has(item.view_id)) {
      uniqueMap.set(item.view_id, item);
    }
  });
return Array.from(uniqueMap.values())
  .sort((a, b) => a.postion - b.postion)
  .map((item) => {
    const refreshDate = new Date(
      item.last_refreashed_time.replace(" ", "T") + "Z",
    );

    return {
      id: `${item.dashboard_id}`,
      type: item.view_type,
      md: view === "dashboards" ? 12 : getAutoMd(item),
      payloadMd: getAutoMd(item),
      viewId: item.view_id,
      position: item.postion,
      data: item.dashboard_data,
      lastRefresh: refreshDate,
      title: item.view_title,
    };
  });
};

const getTableConfig = (newBotMessage) => {
  const columns = newBotMessage?.columns ?? [];
  return {
    headerConfig: [
      {
        cells: columns
          .filter((c) => !c.parent)
          .map((c) => ({
            label: formatLabel(c.name),
            rowSpan: c.colSpan ? undefined : 2,
            colSpan: c.colSpan,
            group: c.group,
            width: c.width ?? 120,
          })),
      },
      {
        cells: columns
          .filter((c) => c.parent)
          .map((c) => ({
            label: formatLabel(c.name),
            parent: c.parent,
            width: c.width ?? 120,
          })),
      },
    ],
    tableRows: (newBotMessage?.rows ?? []).map((row, i) => ({
      id: i,
      cells: (row ?? []).map((cell, ci) => ({
        value: cell ?? "-",
        width: columns[ci]?.width ?? 120,
      })),
    })),
    colors: {
      headerDark: "#354ACD",
      headerMid: "#C2E0FF",
      headerLight: "#354ACD",
      borderDark: "#D9E1E7",
      borderMid: "#4D5F6E",
      borderLight: "#D9E1E7",
      textDark: "#fff",
      textMid: "#1A2025",
      textLight: "#fff",
    },
  };
};

const EMPTY_GRAPH_CLICK = () => {};

const buildWidgetsWithGaps = (widgets) => {
  if (!widgets?.length) return [];
  const result = [];
  let rowSum = 0;
  let gapCount = 0;
  widgets.forEach((w) => {
    const md = Math.max(1, Math.min(12, Number(w.payloadMd) || 6));
    if (rowSum + md > 12) {
      if (rowSum < 12) {
        result.push({
          __gap: true,
          md: 12 - rowSum,
          id: `gap-${gapCount++}`,
        });
      }
      rowSum = 0;
    }
    result.push(w);
    rowSum += md;
    if (rowSum === 12) rowSum = 0;
  });
  if (rowSum > 0 && rowSum < 12) {
    result.push({
      __gap: true,
      md: 12 - rowSum,
      id: `gap-${gapCount++}`,
    });
  }
  return result;
};

// ── Component ──
export default function MyDashboard({
  view = "menu",
  dashboardId = "",
  onDashboardSelect,
  conversationId,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const id = dashboardId || paramId;
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );
const isExpand = view !== "dashboards";
  // ── State ──
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [draggedId, setDraggedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextChat, setHasNextChat] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [updatedOrder, setUpdatedOrder] = useState(null);
  const [dashboardTitle, setDashboardTitle] = useState("");
  const [loadingViewId, setLoadingViewId] = useState(null);
  const [dashboardLastRefreshed, setDashboardLastRefreshed] = useState("");
  const [resizeDragState, setResizeDragState] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [savingChanges, setSavingChanges] = useState(false);
  const convId = conversationId || localStorage.getItem("convId");

  const isValidConvId =
    convId && convId !== "chat" && convId !== "undefined" && convId !== "null";

  // ── Refs ──
  const ghostRef = useRef(null);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const pageRef = useRef(1);
  const resizeRafRef = useRef(null);
  const draggedIdRef = useRef(null);
  const hoveredIdRef = useRef(null);
  const dragRafRef = useRef(null);
  const autoScrollRef = useRef(null);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);

  // ── Stable icon config ──
  const icons = useMemo(
    () => ({
      refresh: RefreshIcon,
      chat: ChatIcon,
      export: ExportIcon,
      expand: ExpandIcon,
      table: TableIcon,
      graph: GraphIcon,
      expandless: ExpandLessIcon,
      DragDrop: DragDropIcon,
      delete: DeleteIcon,
    }),
    [],
  );

  const iconVisibility = useMemo(
    () =>
      isEditMode
        ? {
            showRefresh: false,
            showChat: false,
            showDashboardDropdown: false,
            showViewOptions: false,
            showExpand: false,
            showExport: false,
            ShowDragDrop: true,
            showDelete: true,
          }
        : {
            showRefresh: true,
            showChat: true,
            showDashboardDropdown: false,
            showExpand: true,
            showExport: true,
            showViewOptions: false,
            ShowDragDrop: false,
            showDelete: false,
          },
    [isEditMode],
  );

  // ── Stable callbacks ──
  const handleDeleteClick = useCallback((widgetName) => {
    setSelectedWidget(widgetName);
    setOpenDeleteDialog(true);
  }, []);

  const handleRefresh = useCallback((widgetOrViewIds) => {
    if (Array.isArray(widgetOrViewIds)) {
      setSelectedWidget({ viewIds: widgetOrViewIds });
    } else {
      setSelectedWidget(widgetOrViewIds);
    }
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const closeDeleteDialog = useCallback(() => setOpenDeleteDialog(false), []);
  const closeSuccessSnackbar = useCallback(() => setSuccess(false), []);
  const closeErrorSnackbar = useCallback(() => setError(""), []);

  // ── Append bot message (for table widgets) ──
  const appendBotMessage = useCallback(
    (data, editId, widgetId, position, lastRefresh) => {
      const statement =
        data?.query_result?.statement_response ||
        data?.statement_response ||
        {};
      return {
        newBotMessage: {
          columns: Array.isArray(statement?.manifest?.schema?.columns)
            ? statement.manifest.schema.columns
            : [],
          rows: Array.isArray(statement?.result?.data_array)
            ? statement.result.data_array
            : [],
          viewMode: "table",
          dashboardId: widgetId,
          lastRefreshed: lastRefresh,
          position,
          viewId: widgetId,
        },
        editId,
      };
    },
    [],
  );

  // ── Format graph messages ──
  const formatConversationMessages = useCallback(
    (messages, dashboardId, viewId, position, lastRefresh) =>
      messages.map((msg) => {
        const graph = msg?.graph_data;
        return {
          viewMode: "graph",
          type: msg?.type,
          title: graph?.graph_title,
          variant: graph?.variant,
          xKey: graph?.data_points?.name,
          yKey: graph?.data_points?.value,
          line: graph?.data_points?.line,
          data:
            msg?.type === "heatMap"
              ? graph?.graph_data?.data
              : graph?.graph_data || [],
          styles: graph?.styles || {},
          dashboardId,
          lastRefreshed: lastRefresh,
          position,
          viewId,
        };
      }),
    [],
  );
  // ── Render widget ──
  const renderWidget = useCallback(
    (type, data, widgetId, viewId, position, lastRefresh, title, md) => {
      let newBotMessage = null;
      let graphData = null;

      if (type === "table") {
        ({ newBotMessage } = appendBotMessage(
          data,
          widgetId,
          viewId,
          position,
          lastRefresh,
        ));
      } else if (type === "graph") {
        const msgs = Array.isArray(data) ? data : [data];
        graphData = formatConversationMessages(
          msgs,
          widgetId,
          viewId,
          position,
          lastRefresh,
        );
      }

      const { headerConfig, tableRows, colors } = getTableConfig(newBotMessage);

      const rows1 = tableRows || [];
      const firstRowCells = headerConfig?.[0]?.cells;
      const columns = firstRowCells || headerConfig;
      const firstRow = rows1?.[0] || {};
      const items = columns.map((col, idx) => {
        const valueKey = col?.accessor || col?.key || col?.label;
        let value = firstRow?.[valueKey];
        if (value === undefined) value = firstRow?.cells?.[idx]?.value;
        return {
          title: col?.label || col?.header || "KPI",
          value: value ?? "-",
          year: firstRow?.year ?? "",
        };
      });
      const title1 = items?.[0]?.title;
      const isLoading = loadingViewId === "ALL" || loadingViewId === viewId;
      const tableStatus = true;
      const sharedProps = {
        icons,
        ...iconVisibility,
        showChat: false,
        headerConfig,
        rows: tableRows,
        colors,
        showViewOptions: false,
        onGraphClick: EMPTY_GRAPH_CLICK,
        messageId: [],
        loading: isLoading,
        stretch: true,
        wordLimit: md,
        views: view,
        tableStatus,
      };

      switch (type) {
        case "table":
          return (
            <OverallView
              {...sharedProps}
              title={
                firstRowCells?.length === 1 ? title1 : title || "Order Overview"
              }
              lastRefreshed={`Last Refreshed ${formatDate(newBotMessage?.lastRefreshed)}`}
              msg={[]}
              onDelete={() => handleDeleteClick(newBotMessage)}
              onRefresh={() => handleRefresh(newBotMessage)}
            />
          );
        case "graph":
          return (
            <OverallView
              {...sharedProps}
              title={title || "Sample Graph"}
              lastRefreshed={`Last Refreshed ${formatDate(graphData?.[0]?.lastRefreshed)}`}
              msg={graphData}
              onDelete={() => handleDeleteClick(graphData?.[0])}
              onRefresh={() => handleRefresh(graphData?.[0])}
            />
          );
        default:
          return null;
      }
    },
    [
      icons,
      iconVisibility,
      loadingViewId,
      appendBotMessage,
      formatConversationMessages,
      handleDeleteClick,
      handleRefresh,
    ],
  );

  // ── Fetch dashboard list ──

  const fetchDashboardListById = useCallback(
    async (page, force = false) => {
      if (!userInfo?.id) return;
      if (!force && !hasNextChat) return;
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;

      try {
        if (page === 1) {
          setInitialLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        const response = await getDashboardListById(
          {
            user_id: userInfo.id,
            user_name: userInfo.name,
            user_email: userInfo.email,
            dashboard_id: id,
          },
          page,
        );

        // ✅ SAFE PARSING START
        let safeData;

        try {
          const rawText =
            typeof response === "string" ? response : JSON.stringify(response);

          if (!rawText) {
            console.error("Empty response");
            return;
          }

          const cleanedText = rawText
            .replace(/\bNaN\b/g, "null")
            .replace(/\bundefined\b/g, "null")
            .replace(/\bInfinity\b/g, "null");

          safeData = JSON.parse(cleanedText);
        } catch (e) {
          console.error("JSON parse failed:", e);

          // fallback
          safeData = response;

          if (!safeData) {
            throw new Error("Invalid API response");
          }
        }
        // ✅ SAFE PARSING END

        // 👉 use safeData instead of data
        setHasNextChat(safeData?.has_next ?? false);
        setDashboardTitle(safeData?.dashboard_title);

      const latestRefresh = safeData?.views?.reduce((latest, view) => {
        if (!view?.last_refreashed_time) return latest;

        // Treat backend time as UTC
        const currentDate = new Date(
          view.last_refreashed_time.replace(" ", "T") + "Z",
        );

        return !latest || currentDate > latest ? currentDate : latest;
      }, null);

        setDashboardLastRefreshed(latestRefresh);

        const formattedWidgets = formatWidgets(safeData?.views || [], view);

        setWidgets((prev) =>
          page === 1 ? formattedWidgets : [...prev, ...formattedWidgets],
        );

        pageRef.current = page;
        setPageNumber(page);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        if (page === 1) setInitialLoading(false);
        else setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [userInfo, id, hasNextChat],
  );

  // ── Resize ──
  const startResize = useCallback((e, widgetPosition) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerLeft = containerRect.left;
    const startX = e.clientX;

    const card = e.target.closest("[data-widget-id]");
    if (!card) return;
    const cardLeft = card.getBoundingClientRect().left;
    const startWidth = card.getBoundingClientRect().width;
    const maxWidth = containerLeft + containerWidth - cardLeft;

    card.style.willChange = "width";
    card.style.transition = "none";
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    setResizeDragState(widgetPosition);

    const onMove = (moveEvent) => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(() => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = Math.min(startWidth + deltaX, maxWidth);
        card.style.width = `${Math.max(containerWidth * 0.25, newWidth)}px`;
      });
    };

    const onUp = (upEvent) => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      const deltaX = upEvent.clientX - startX;
      const newWidth = Math.min(startWidth + deltaX, maxWidth);
      const widthPercent = (newWidth / containerWidth) * 100;
      let snappedMd;
      if (widthPercent <= 30) snappedMd = 3;
      else if (widthPercent <= 40) snappedMd = 4;
      else if (widthPercent <= 75) snappedMd = 6;
      else snappedMd = 12;

      card.style.width = "";
      card.style.willChange = "";
      card.style.transition = "";

      setWidgets((prev) => {
        const updated = prev.map((w) =>
          w.position === widgetPosition ? { ...w, md: snappedMd } : w,
        );
        setUpdatedOrder(
          updated.map((w) => ({
            view_id: Number(w.viewId),
            view_size: w.md,
          })),
        );
        return updated;
      });
      setHasChanges(true);
      setResizeDragState(null);

      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  // ── Drag and drop ──
  const startDrag = useCallback(
    (e, widgetId) => {
      if (!isEditMode) return;
      e.preventDefault();

      const numericId = Number(widgetId);
      draggedIdRef.current = numericId;
      hoveredIdRef.current = numericId;

      const card = e.currentTarget.closest("[data-widget-id]");
      if (!card) return;

      const rect = card.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      const startX = e.clientX;
      const startY = e.clientY;
      let hasDragged = false;
      let ghost = null;

      // When the direct hit-test lands on whitespace (no widget beneath the
      // cursor), pick the nearest widget by squared distance from its center
      // so the drop still reorders cards into empty gaps.
      const findNearestWidgetId = () => {
        const mx = mouseXRef.current;
        const my = mouseYRef.current;
        const cards = containerRef.current
          ? containerRef.current.querySelectorAll("[data-widget-id]")
          : [];
        let bestId = null;
        let bestDist = Infinity;
        for (const c of cards) {
          const tid = Number(c.getAttribute("data-widget-id"));
          if (tid === draggedIdRef.current) continue;
          const r = c.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const d = (mx - cx) * (mx - cx) + (my - cy) * (my - cy);
          if (d < bestDist) {
            bestDist = d;
            bestId = tid;
          }
        }
        return bestId;
      };

      const updateHoverTarget = () => {
        if (!ghost) return;
        ghost.style.visibility = "hidden";
        const el = document.elementFromPoint(
          mouseXRef.current,
          mouseYRef.current,
        );
        ghost.style.visibility = "";
        const target = el?.closest("[data-widget-id]");
        let tid = target ? Number(target.getAttribute("data-widget-id")) : null;
        if (tid == null || tid === draggedIdRef.current) {
          tid = findNearestWidgetId();
        }
        if (tid != null && tid !== hoveredIdRef.current) {
          hoveredIdRef.current = tid;
          setHoveredId(tid);
        }
      };

      const startAutoScroll = () => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const EDGE_ZONE = 120; // larger zone for easier triggering
        const MIN_SPEED = 2;
        const MAX_SPEED = 24; // faster max speed

        // Quadratic easing — speed ramps up exponentially near edge
        const computeSpeed = (distance) => {
          if (distance <= 0) return MAX_SPEED; // beyond the edge → max speed
          const ratio = 1 - Math.min(distance / EDGE_ZONE, 1);
          // ease-in-quad: more responsive near the edge
          const eased = ratio * ratio;
          return MIN_SPEED + (MAX_SPEED - MIN_SPEED) * eased;
        };

        const tick = () => {
          const scrollRect = scrollEl.getBoundingClientRect();
          const y = mouseYRef.current;
          const distTop = y - scrollRect.top;
          const distBottom = scrollRect.bottom - y;
          let didScroll = false;

          // Scroll up — also works when mouse is ABOVE the container
          if (distTop < EDGE_ZONE) {
            const prev = scrollEl.scrollTop;
            scrollEl.scrollTop -= computeSpeed(distTop);
            if (scrollEl.scrollTop !== prev) didScroll = true;
          }
          // Scroll down — also works when mouse is BELOW the container
          else if (distBottom < EDGE_ZONE) {
            const prev = scrollEl.scrollTop;
            scrollEl.scrollTop += computeSpeed(distBottom);
            if (scrollEl.scrollTop !== prev) didScroll = true;
          }

          if (didScroll) updateHoverTarget();
          autoScrollRef.current = requestAnimationFrame(tick);
        };
        autoScrollRef.current = requestAnimationFrame(tick);
      };

      const onMove = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (!hasDragged && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

        if (!hasDragged) {
          hasDragged = true;
          ghost = card.cloneNode(true);
          ghost.style.cssText = `position:fixed;top:0;left:0;width:${rect.width}px;height:${rect.height}px;opacity:0.95;pointer-events:none;z-index:9999;box-shadow:0 20px 50px rgba(0, 102, 204, 0.25),0 0 0 2px rgba(0, 102, 204, 0.4);border-radius:12px;will-change:transform;transition:none;transform:translate(${rect.left}px,${rect.top}px) rotate(2deg) scale(1.02);transform-origin:center;background:#fff;`;
          document.body.appendChild(ghost);
          ghostRef.current = ghost;
          document.body.style.cursor = "grabbing";
          document.body.style.userSelect = "none";
          setDraggedId(numericId);
          startAutoScroll();
        }

        mouseXRef.current = moveEvent.clientX;
        mouseYRef.current = moveEvent.clientY;

        if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = requestAnimationFrame(() => {
          if (!ghost) return;
          ghost.style.transform = `translate(${moveEvent.clientX - offsetX}px,${moveEvent.clientY - offsetY}px) rotate(2deg) scale(1.02)`;
          updateHoverTarget();
        });
      };

      const onUp = () => {
        if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
        if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        if (hasDragged) {
          const from = draggedIdRef.current;
          const to = hoveredIdRef.current;
          if (from != null && to != null && from !== to) {
            setWidgets((prev) => {
              const items = [...prev];
              const fromIdx = items.findIndex(
                (w) => Number(w.position) === from,
              );
              const toIdx = items.findIndex((w) => Number(w.position) === to);
              if (fromIdx !== -1 && toIdx !== -1) {
                const [moved] = items.splice(fromIdx, 1);
                items.splice(toIdx, 0, moved);
              }
              setUpdatedOrder(
                items.map((w) => ({
                  view_id: Number(w.viewId),
                  view_size: w.payloadMd,
                })),
              );
              return items;
            });
            setHasChanges(true);
          }
          if (ghost?.parentNode) document.body.removeChild(ghost);
          ghostRef.current = null;
        }

        draggedIdRef.current = null;
        hoveredIdRef.current = null;
        setDraggedId(null);
        setHoveredId(null);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [isEditMode],
  );

  // ── Scroll handler ──
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        !isFetchingRef.current &&
        hasNextChat &&
        id
      ) {
        fetchDashboardListById(pageRef.current + 1, false);
      }
    },
    [hasNextChat, id, fetchDashboardListById],
  );

  // ── Delete (deferred until Save Changes) ──
  const confirmDelete = useCallback(() => {
    const viewId = selectedWidget?.viewId;
    if (viewId == null) {
      setOpenDeleteDialog(false);
      return;
    }
    setWidgets((prev) => {
      const remaining = prev.filter((w) => w.viewId !== viewId);
      setUpdatedOrder(
        remaining.map((w) => ({
          view_id: Number(w.viewId),
          view_size: w.md,
        })),
      );
      return remaining;
    });
    setPendingDeleteIds((prev) =>
      prev.includes(viewId) ? prev : [...prev, viewId],
    );
    setOpenDeleteDialog(false);
    setHasChanges(true);
  }, [selectedWidget]);

  // ── Commit pending deletions + order update on Save Changes ──
  const commitChanges = useCallback(async () => {
    if (savingChanges) return;
    setSavingChanges(true);
    try {
      for (const viewId of pendingDeleteIds) {
        await deleteDashboardListV2({
          user_id: userInfo.id,
          user_name: userInfo.name,
          user_email: userInfo.email,
          dashboard_id: Number(id),
          view_id: viewId,
        });
      }
      if (updatedOrder) {
        await updateWidgetOrder(updatedOrder);
      }
      if (pendingDeleteIds.length > 0) {
        setSuccessMessage("Dashboard Deleted Successfully");
        setSuccess(true);
      }
      setPendingDeleteIds([]);
      setUpdatedOrder(null);
      setHasChanges(false);
      setIsEditMode(false);
      setError("");
      pageRef.current = 1;
      setPageNumber(1);
      setHasNextChat(true);
      await fetchDashboardListById(1, true);
    } catch (err) {
      console.error("Save dashboard changes failed:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save changes. Please try again.",
      );
    } finally {
      setSavingChanges(false);
    }
  }, [
    savingChanges,
    pendingDeleteIds,
    updatedOrder,
    userInfo,
    id,
    fetchDashboardListById,
  ]);

  // ── Cancel edit (refetch if there are pending deletions to restore UI) ──
  const cancelEdit = useCallback(async () => {
    const hadPendingDeletes = pendingDeleteIds.length > 0;
    setIsEditMode(false);
    setHasChanges(false);
    setUpdatedOrder(null);
    setPendingDeleteIds([]);
    if (hadPendingDeletes) {
      pageRef.current = 1;
      setPageNumber(1);
      setHasNextChat(true);
      await fetchDashboardListById(1, true);
    }
  }, [pendingDeleteIds, fetchDashboardListById]);

  // ── Save order ──
  const updateWidgetOrder = useCallback(
    async (newOrder) => {
      try {
        await dashboardOrderPlacementV2({
          user_id: userInfo.id,
          user_name: userInfo.name,
          user_email: userInfo.email,
          dashboard_id: Number(id),
          new_order: newOrder.map((item) => item.view_id),
          view_size: newOrder.map((item) => ({
            view_id: item.view_id,
            view_size: item.view_size,
          })),
          page: pageNumber,
        });
        setSuccessMessage("Updated Successfully");
        setSuccess(true);
        setError("");
      } catch (err) {
        console.error("Error updating dashboard order:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to update order. Please try again.",
        );
      }
    },
    [userInfo, id, pageNumber],
  );

  // ── Effects ──
  useEffect(() => {
    const refreshDashboard = async () => {
      if (!refreshTrigger) return;
      const viewIds = selectedWidget?.viewIds
        ? selectedWidget.viewIds
        : [selectedWidget?.viewId];
      if (!viewIds?.length) return;

      try {
        setLoadingViewId(viewIds.length === 1 ? viewIds[0] : "ALL");
        const response = await dashboardRefresh({
          user_id: userInfo?.id,
          user_name: userInfo?.name,
          user_email: userInfo?.email,
          dashboard_id: Number(id),
          view_ids: viewIds,
        });
        setDashboardTitle(response?.data?.dashboard_title);
        setDashboardLastRefreshed(response?.data?.last_refreashed_time);
        const updatedWidgets = formatWidgets(
          response?.data?.refreshed_data,
          view,
        );

        const latestRefresh = updatedWidgets?.reduce((latest, widget) => {
          if (!widget?.lastRefresh) return latest;

          return !latest || new Date(widget.lastRefresh) > new Date(latest)
            ? widget.lastRefresh
            : latest;
        }, null);

        setDashboardLastRefreshed(latestRefresh);
        setWidgets((prev) =>
          prev.map((widget) => {
            const updated = updatedWidgets.find(
              (w) => w.viewId === widget.viewId,
            );
            return updated ? { ...widget, ...updated } : widget;
          }),
        );
        setSuccessMessage("Dashboard Refreshed Successfully");
      } catch (err) {
        console.error("Failed to refresh dashboard:", err);
        setError("Failed to refresh dashboard. Please try again.");
      } finally {
        setLoadingViewId(null);
      }
    };
    refreshDashboard();
  }, [refreshTrigger, selectedWidget, id, userInfo]);

  useEffect(() => {
    setPageNumber(1);
    fetchDashboardListById(1, true);
  }, []); 

  // ── Render ──
  return (
    <Box
      sx={{
        height: "calc(100vh - 44px)",
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: "16px", sm: "24px", md: "24px", lg: "24px" },
          pt: { xs: "24px", sm: "24px", md: "24px", lg: "24px" },
          pb: { xs: 2, md: 3, lg: 3 },
          background: "#FFFFFF",
          zIndex: 10,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box
            display="flex"
            gap={{ xs: 1.5, md: 2, lg: 3 }}
            alignItems="center"
          >
            <button className={styles.MyDashboardBtn}>
              {!isEditMode && (
                <Box
                  component="img"
                  src={BackArrow}
                  mr={1}
                  onClick={() => {
                    if (location.pathname !== `/samarth/mydashoard/${id}`) {
                      navigate(
                        isValidConvId
                          ? `/samarth/chat/${convId}`
                          : "/samarth/chat",
                        {
                          state: {
                            view: "dashboards",
                            conversationId: isValidConvId ? convId : null,
                          },
                          replace: true,
                        },
                      );

                      onDashboardSelect?.();
                    } else {
                      navigate("/samarth/dashoard-list", {
                        state: {
                          conversationId: isValidConvId ? convId : null,
                        },
                      });
                    }
                  }}
                  sx={{
                    width: { sm: 16, md: 20, lg: 24 },
                    height: { sm: 16, md: 20, lg: 24 },
                  }}
                />
              )}
              {!isEditMode ? dashboardTitle : `Edit ${dashboardTitle}`}
            </button>
            {!isEditMode &&
              dashboardLastRefreshed &&
              formatDateTime(dashboardLastRefreshed).length > 0 && (
                <p className={styles.lastRefreshed}>
                  {formatDateTime(dashboardLastRefreshed)}
                </p>
              )}
          </Box>

          <Box display="flex" gap={2}>
            {!isEditMode ? (
              <Box display="flex">
                {/* Refresh */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    p: "4px",
                    cursor: "pointer",

                    "&:hover .refreshLabel": {
                      opacity: 1,
                      visibility: "visible",
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Typography
                    className="refreshLabel"
                    sx={{
                      position: "absolute",
                      right: "120%",
                      // mr: 1,
                      fontSize: { md: "10px", lg: "14px" },
                      fontWeight: 400,
                      color: "#0066CC",
                      opacity: 0,
                      visibility: "hidden",
                      transform: "translateX(10px)",
                      transition: "all 0.25s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Refresh
                  </Typography>

                  <Box
                    component="img"
                    src={RefreshIcon}
                    sx={{
                      height: { xs: "16px", md: "24px", lg: "32px" },
                      width: { xs: "16px", md: "24px", lg: "32px" },
                      border: "1px solid #0066CC",
                      borderRadius: "50%",
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "#C2E0FF",
                        borderRadius: "50%",
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefresh(widgets.map((w) => w.viewId));
                    }}
                  />
                </Box>

                {/* Edit */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "6px",
                    p: "4px",
                    cursor: "pointer",

                    "&:hover .editLabel": {
                      opacity: 1,
                      visibility: "visible",
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Typography
                    className="editLabel"
                    sx={{
                      position: "absolute",
                      right: { md: "100%", lg: "120%" },
                      mr: 5,
                      fontSize: { md: "10px", lg: "14px" },
                      fontWeight: 400,
                      color: "#0066CC",
                      opacity: 0,
                      visibility: "hidden",
                      transform: "translateX(10px)",
                      transition: "all 0.25s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Edit
                  </Typography>

                  <Box
                    component="img"
                    src={EditIcon}
                    sx={{
                      height: { xs: "16px", md: "24px", lg: "32px" },
                      width: { xs: "16px", md: "24px", lg: "32px" },
                      border: "1px solid #0066CC",
                      borderRadius: "50%",
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "#C2E0FF",
                        borderRadius: "50%",
                      },
                    }}
                    onClick={() => {
                      setIsEditMode(true);
                      setHasChanges(false);
                    }}
                  />
                </Box>

                {/* Expand / Minimize */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    p: "4px",
                    cursor: "pointer",

                    "&:hover .actionLabel": {
                      opacity: 1,
                      visibility: "visible",
                      transform: "translateY(0)",
                    },
                  }}
                >
                  <Typography
                    className="actionLabel"
                    sx={{
                      position: "absolute",
                      right: "120%",
                      mr: 10,
                      fontSize: { md: "10px", lg: "14px" },
                      fontWeight: 400,
                      color: "#0066CC",
                      opacity: 0,
                      visibility: "hidden",
                      transform: "translateX(10px)",
                      transition: "all 0.25s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isExpand ? "Minimize" : "Expand"}
                  </Typography>

                  <Box
                    component="img"
                    src={isExpand ? ExpandLessIcon : ExpandIcon}
                    sx={{
                      height: { xs: "16px", md: "24px", lg: "32px" },
                      width: { xs: "16px", md: "24px", lg: "32px" },
                      border: "1px solid #0066CC",
                      borderRadius: "50%",
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "#C2E0FF",
                        borderRadius: "50%",
                      },
                    }}
                    onClick={() => {
                      if (isExpand) {
                        navigate(
                          isValidConvId
                            ? `/samarth/chat/${convId}`
                            : "/samarth/chat",
                          {
                            state: {
                              view: "dashboards",
                              selectedId: id,
                              conversationId: isValidConvId ? convId : null,
                            },
                            replace: true,
                          },
                        );
                      } else {
                        if (
                          conversationId !== null &&
                          conversationId !== undefined
                        ) {
                          localStorage.setItem("convId", conversationId);
                        }

                        navigate(`/samarth/mydashoard/${id}`, {
                          state: {
                            view: "dashboards",
                          },
                        });
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <>
                <button
                  className={styles.cancelBtn}
                  onClick={cancelEdit}
                  disabled={savingChanges}
                >
                  Cancel
                </button>

                <button
                  className={styles.saveBtn}
                  disabled={!hasChanges || savingChanges}
                  onClick={commitChanges}
                >
                  {savingChanges ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Scrollable Widget Area */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: "16px", sm: "24px", md: "24px", lg: "24px" },
          py: 2,
        }}
      >
        <div ref={containerRef} className={styles.widgetGrid}>
          {initialLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "calc(100vh - 150px)",
              }}
            >
              <CircularProgress />
            </Box>
          ) : widgets.length > 0 ? (
            (isEditMode ? buildWidgetsWithGaps(widgets) : widgets).map(
              (widget) => {
                if (widget.__gap) {
                  const gapMdClass =
                    widget.md === 12
                      ? styles.widgetFull
                      : widget.md <= 3
                        ? styles.widgetQuarter
                        : widget.md === 4
                          ? styles.widgetThird
                          : styles.widgetHalf;
                  return (
                    <div
                      key={widget.id}
                      className={`${styles.widgetCard} ${gapMdClass} ${styles.widgetDropZone} ${
                        draggedId !== null ? styles.widgetDropZoneActive : ""
                      }`}
                    >
                      Drop here
                    </div>
                  );
                }
                const pos = Number(widget.position);
                const isDragged = draggedId === pos;
                const isHovered = hoveredId === pos && draggedId !== pos;
                const isAnyDragging = draggedId !== null;
                const isBeingResized = resizeDragState === pos;
                const mdClass =
                  widget.md === 12
                    ? styles.widgetFull
                    : widget.md <= 3
                      ? styles.widgetQuarter
                      : widget.md === 4
                        ? styles.widgetThird
                        : styles.widgetHalf;
                return (
                  <div
                    key={widget.position}
                    data-widget-id={widget.position}
                    className={`${styles.widgetCard} ${mdClass}`}
                    style={{
                      opacity: isDragged ? 0.25 : 1,
                      outline: isHovered
                        ? "2px solid #0066CC"
                        : isEditMode
                          ? "1px dashed #C2E0FF"
                          : "2px solid transparent",
                      outlineOffset: isHovered ? "2px" : "0",
                      backgroundColor: isHovered
                        ? "rgba(0, 102, 204, 0.04)"
                        : "transparent",
                      transform:
                        isHovered && isAnyDragging
                          ? "scale(1.015)"
                          : "scale(1)",
                      boxShadow: isHovered
                        ? "0 8px 24px rgba(0, 102, 204, 0.12)"
                        : "none",
                      transition: isBeingResized
                        ? "none"
                        : "transform 0.2s ease, outline-color 0.15s ease, box-shadow 0.2s ease, opacity 0.2s ease, background-color 0.15s ease",
                      ...(isBeingResized && {
                        willChange: "width",
                        userSelect: "none",
                      }),
                      cursor: isEditMode
                        ? isDragged
                          ? "grabbing"
                          : "grab"
                        : "default",
                    }}
                    onMouseDown={(e) => {
                      if (!e.target.closest("[data-drag-handle]")) return;
                      startDrag(e, widget.position);
                    }}
                  >
                    {renderWidget(
                      widget.type,
                      widget.data,
                      widget.id,
                      widget.viewId,
                      widget.position,
                      widget.lastRefresh,
                      widget.title,
                      widget.md,
                    )}
                    {isEditMode && (
                      <div
                        className={styles.resizeHandle}
                        onMouseDown={(e) => startResize(e, widget.position)}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                        >
                          <path
                            d="M9 1L1 9M9 5L5 9M9 9L9 9"
                            stroke="#94A3B8"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              },
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: "100%",
                height: "calc(100vh - 150px)",
              }}
            >
              <Typography
                fontSize="18px"
                color="#94A3B8"
                textAlign="center"
                py={2}
              >
                No Data Available
              </Typography>
            </Box>
          )}
        </div>

        {isLoadingMore && pageRef.current > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <DeleteChatDialog
        open={openDeleteDialog}
        title="Are you sure you want to delete Graph?"
        description="This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
      <ReusableSnackbar
        open={success}
        message={successMessage}
        type="success"
        onClose={closeSuccessSnackbar}
      />
      {typeof error === "string" && error.length > 0 && (
        <ReusableSnackbar
          open={!!error}
          message={error}
          type="error"
          onClose={closeErrorSnackbar}
        />
      )}
    </Box>
  );
}